/*
  The Proper Pet — scroll-driven 4-layer construction viewer.

  Real 3D: a glTF binary (assets/proper-pet-4layers.glb, four meshes plus
  one baked "explode" AnimationClip THAT IS NEVER READ — see below)
  rendered with a self-hosted three.js, not Google's <model-viewer>.

  Why not <model-viewer>: it was tried first. Root-caused, live, against
  the deployed page: <model-viewer>'s `currentTime` (the property meant to
  scrub the baked clip) silently resets to 0 on every one of its own
  internal render frames once the element is in view and interactive
  (`camera-controls` enabled) — verified by holding scroll at full progress
  and reading `currentTime` back as 0 instead of the expected clip
  duration, repeatedly, under both documented workarounds (paused-then-
  assign, and playing-at-timeScale-0-assign-every-frame). A manual override
  only ever stuck when the element was scrolled OUT of view, i.e. when
  nothing was actively re-rendering it — proof the bug was model-viewer's
  own internal ticker fighting external writes, not this file's scroll
  math (which was already correct and is reused below almost verbatim).
  Separately, the baked clip's own displacement was wrong regardless: the
  pad's mesh footprint is ~2.44 x 1.84 units, but the clip moved the top
  layer to y=2.67 — more than the pad's own width, not an exploded
  diagram, a layer flying off into space. Both bugs are avoidable by not
  depending on <model-viewer>'s named-animation API at all: this file owns
  a single render loop, drives each mesh's `position.y` directly every
  frame from scroll progress, and never touches the GLB's animation clip.

  Self-hosted (this project's standing rule, no third-party CDN):
  three.module.min.js, GLTFLoader.js (+ its two hard transitive deps,
  BufferGeometryUtils.js and SkeletonUtils.js), RoomEnvironment.js. No
  import map: Horizon's own theme.liquid already emits one for its native
  @theme/* module system, and only the first <script type="importmap"> in
  a document is honored — a second one was tried first and silently
  ignored (real bug, found live: the model never mounted, nothing fetched,
  no console error). Fixed by editing GLTFLoader/BufferGeometryUtils/
  SkeletonUtils/RoomEnvironment's own imports to a relative
  `./three.module.min.js` instead of the bare "three" specifier — needs no
  import map at all, since every file sits flat in the same assets/ folder.
  Fetched lazily via a dynamic import, inside the same IntersectionObserver
  that already gated <model-viewer>'s load, so the ~121KB gzipped core
  never loads until the section nears the viewport — verified in practice
  (network panel), not just by file size, per GAPS.md.

  Progressive enhancement, unchanged in spirit from the model-viewer build:
    1. No JS at all      -> the CSS layer stack and every layer's text.
    2. JS, no WebGL/3D   -> same, plus the scroll-linked step highlighting.
    3. Full support      -> the CSS stack is swapped for the live 3D model.
  Nothing is hidden behind the 3D. Reduced motion pins the model to its
  fully-exploded pose and never animates it.
*/
(function () {
  var scenes = document.querySelectorAll('[data-pp-layers]');
  if (!scenes.length) return;

  var reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Below the theme's own 990px breakpoint the pinned scrub is switched off
     and the model is rendered once, in a fixed pose.

     Why: the scroll track is 420vh. On a desktop that buys a 4-screen
     scrubbed explode with the stage pinned in front of you. On a phone or a
     tablet it buys 4 screens of scrolling past an image that barely changes -
     the stage is smaller, the explode covers less distance on screen, and
     touch scrolling is coarse enough that the intermediate frames never
     read. Reported from a real device as "the image sits still, you scroll
     through nothing, and you see nothing".

     A 4-screen tunnel that shows nothing is worse than no tunnel, so small
     screens take the same treatment reduced motion already takes: normal
     section height, no sticky, one static pose, and the four step buttons
     still tappable to switch layers. The information survives; only the
     scroll cost goes.

     Read once at init, matching how `reduced` is read. A resize across the
     breakpoint is a reload case, not a live-reflow case. */
  var smallScreen = window.matchMedia('(max-width: 989px)').matches;
  var staticPose = reduced || smallScreen;

  // Shopify's CDN runs classic theme-asset .js files through a server-side
  // transform that appears to token-match dynamic-import syntax textually
  // (not a real parse) and rewrite it for a CommonJS target — even for a
  // runtime-variable call that can't actually be bundled. Deployed output
  // was confirmed (live, via the CDN) to carry an esbuild CJS-interop
  // preamble and to rewrite the call into a `require(...)` call, which
  // doesn't exist in a browser and threw `ReferenceError: require is not
  // defined` on every load. Never reproduced locally (`node --check` is
  // clean, no `require` anywhere in source) — a hosting-platform transform,
  // not a bug in this file. Worked around below by assembling the dynamic
  // import from two separately-quoted string pieces at runtime, so the
  // literal keyword-plus-paren sequence never appears contiguously
  // anywhere in this file's own bytes for the scanner to match — including
  // inside this comment, deliberately worded to avoid it too.
  var dynImport = new Function('u', 'return ' + 'import' + '(u);');

  scenes.forEach(function (scene) {
    try {
      var track = scene.querySelector('[data-pp-track]');
      var stage = scene.querySelector('[data-pp-stage]');
      var mount = scene.querySelector('[data-pp-3d-mount]');
      var layers = Array.prototype.slice.call(scene.querySelectorAll('[data-pp-layer]'));
      var panels = Array.prototype.slice.call(scene.querySelectorAll('[data-pp-panel]'));
      var steps = Array.prototype.slice.call(scene.querySelectorAll('[data-pp-step]'));
      if (!track || !stage || !layers.length) return;

      var count = layers.length;
      var modelSrc = scene.getAttribute('data-pp-model');
      var threeUrl = scene.getAttribute('data-pp-three-url');
      var gltfLoaderUrl = scene.getAttribute('data-pp-gltf-loader-url');
      var roomEnvUrl = scene.getAttribute('data-pp-room-env-url');

      // Motion (motion.dev, vanilla build) is loaded as a theme asset and
      // exposes window.Motion. Used for the DOM panel/step transitions
      // only — unrelated to the 3D fix, unchanged from before.
      var M = window.Motion || null;
      var spring = M ? { type: 'spring', stiffness: 260, damping: 30 } : null;

      // The live three.js scene, once mounted. Everything three.js-owned
      // lives on this one object so there is exactly one place that can
      // hold stale/half-initialized state.
      var three = null; // { renderer, camera, meshes, restY, radius, target, canvas, spherical }
      var progress = 0;

      scene.classList.add('is-live');

      var dragY = 0;
      var dragX = 0;
      var activeIndex = -1;

      function setActive(i) {
        if (i === activeIndex) return;
        activeIndex = i;
        layers.forEach(function (el, n) {
          el.classList.toggle('is-active', n === i);
          el.classList.toggle('is-dimmed', i !== -1 && n !== i);
        });
        panels.forEach(function (el, n) {
          var on = n === i;
          el.classList.toggle('is-active', on);
          if (M && M.animate) {
            // Spring, not a duration curve: the panel should settle like a
            // physical card, and springs stay smooth if the user scrolls
            // back before it finished.
            M.animate(el,
              { opacity: on ? 1 : 0, transform: on ? 'translateY(0px)' : 'translateY(10px)' },
              spring);
          }
        });
        steps.forEach(function (el, n) {
          var on = n === i;
          el.classList.toggle('is-active', on);
          el.setAttribute('aria-current', on ? 'step' : 'false');
          if (M && M.animate) {
            M.animate(el, { opacity: on ? 1 : 0.45 }, spring);
          }
        });
      }

      // Critically-damped spring smoother. The raw scroll value is exact but
      // jumpy on a trackpad flick; chasing it with a spring is what makes the
      // stack feel like it has mass instead of being dragged frame by frame.
      var shown = 0;      // the value actually rendered
      var vel = 0;        // spring velocity
      var target = 0;     // the value scroll wants

      function stepSpring(dt) {
        var stiffness = 120;
        var damping = 22;
        var a = stiffness * (target - shown) - damping * vel;
        vel += a * dt;
        shown += vel * dt;
        if (Math.abs(target - shown) < 0.0002 && Math.abs(vel) < 0.0002) {
          shown = target;
          vel = 0;
        }
        return shown;
      }

      // Amplitude of separation added to each layer's own rest position,
      // authored here directly rather than read from the GLB's baked clip
      // (bypassed entirely). Roughly preserves the original animator's
      // relative spacing ratio (0.177 : 0.092 : 0.05 : 0) rescaled into a
      // total spread well under the pad's own ~1.84-2.44 unit footprint,
      // instead of the baked clip's 2.67-unit runaway.
      // Widened hard, and this is the change that makes every texture on
      // this model visible for the first time.
      //
      // At 0.72/0.46/0.22 the layers parted just enough to be counted, and
      // each one's top face stayed hidden under the layer above. Every map
      // added over three rounds - the weave, the felt fibre, the dimpled
      // core, the dot grid - lives on those top faces. All of it was being
      // rendered into a surface the camera could never see.
      //
      // At this spread the faces swing into shot, so the materials finally
      // do the work they were built for. It also matches the owner's
      // reference diagram, where the four layers float clearly apart rather
      // than sitting in a near-closed stack.
      var EXPLODE = [1.7, 1.05, 0.5, 0];
      var LAYER_NODE_NAMES = [
        'Soft_and_Breathable_Fabric',
        'Max_Absorbency_Material',
        'Breathable_Waterproofing_Layer',
        'Anti_Slip_Gel_Rubber_Layer'
      ];

      // Real relative thickness, corrected by the owner against the physical
      // product. My first pass had the top two layers the wrong way round.
      // Top to bottom, as it actually lies on the floor:
      //
      //   1 printed face      90 gsm quick-dry polyester mesh   -> thin
      //   2 absorbent core   280 gsm needle-punched nonwoven    -> THICKEST
      //   3 barrier          TPU film, 0.025 mm (~60 gsm)       -> a film
      //   4 backing          silicone dots, 0.3-0.5 mm high     -> medium
      //
      // The face is the thin one and the core underneath it is the bulk -
      // which is the whole point of the product, and I had it inverted.
      // Scaled on Y only, so footprint and UVs are untouched.
      var THICKNESS = [0.34, 1.0, 0.08, 0.30];

      function render(p) {
        progress = p;

        // CSS stack pose. Stays in sync even once the 3D model is live, so
        // the fallback is never stale if WebGL drops out mid-session.
        var rotY = -34 + p * 46 + dragY;
        var rotX = Math.max(-70, Math.min(8, -26 + p * 16 + dragX));
        stage.style.transform = 'rotateX(' + rotX + 'deg) rotateY(' + rotY + 'deg)';
        stage.style.setProperty('--pp-explode', Math.min(1, p / 0.34).toFixed(3));

        var scrub = Math.min(1, p / 0.55);

        if (three && three.ready) {
          for (var i = 0; i < LAYER_NODE_NAMES.length; i++) {
            var mesh = three.meshes[LAYER_NODE_NAMES[i]];
            if (mesh) mesh.position.y = three.restY[i] + EXPLODE[i] * scrub;
          }
          // Same azimuth/polar formula <model-viewer>'s cameraOrbit used —
          // a direct port, not a redesign. Polar angle increases with
          // progress so the growing gaps between layers stay visible as
          // the stack separates, rather than being viewed from directly
          // above once it's fully exploded.
          var theta = three.degToRad(-28 + p * 40 + dragY);
          // 64, after a round at 74 that went too far. 74 put the camera
          // almost level with the stack, which shows the layer edges but
          // hides the top faces - and the top faces are where every material
          // map, the dot grid and the inter-layer shadows actually live. All
          // that work was invisible at 74.
          //
          // The reference photo sits at three-quarters for exactly this
          // reason: you need the edges for thickness AND the faces for
          // material. 64 keeps both.
          var phi = three.degToRad(64 + p * 8);
          /* Pull back as the stack opens, on the same curve the layers travel,
             so the subject keeps filling the frame instead of shrinking into it. */
          var open = Math.min(1, p / 0.55);
          var dist = (three.radiusRest == null || three.radiusOpen == null)
            ? three.radius
            : three.radiusRest + (three.radiusOpen - three.radiusRest) * open;
          three.spherical.set(dist, phi, theta);
          three.camera.position.setFromSpherical(three.spherical).add(three.target);
          three.camera.lookAt(three.target);
          three.renderer.render(three.scene, three.camera);
        }

        /* Layer 1 is active from the first frame, not after 16% of the scrub.
           The gate used to be `p > 0.16`, which meant the frame a visitor first
           sees had no active layer at all: every row in the list greyed out, the
           description card absent, and the lower half of the glass panel empty.
           The pad reads as a blank cream slab in a large empty area, and the
           print - the whole argument of the product - is not called out anywhere.

           This was reported as "the first frames are wrong" three times while
           every check ran mid-scrub, where a layer is already active and the
           composition is complete. The camera, the box and the sticky frame were
           each corrected in turn; none of them was this.

           The scrub still walks the four layers across its full travel - the
           mapping below just starts at the first one instead of at nothing. */
        var idx = Math.floor((p / 0.84) * count);
        idx = Math.max(0, Math.min(count - 1, idx));
        setActive(idx);
      }

      /* ---------- load the real 3D model, only when it's near ---------- */
      function mountModel() {
        if (three || !mount || !modelSrc || !threeUrl || !gltfLoaderUrl) return;
        three = { ready: false };

        var canvas = document.createElement('canvas');
        canvas.className = 'pp-3d__model';
        mount.appendChild(canvas);

        // A dynamic import (via dynImport, defined above), not a static
        // <script type="module">: a static module tag fetches its whole
        // graph at parse time regardless of
        // scroll position, which would undo the lazy-load this section is
        // built around. Every module is fetched by its own self-hosted
        // asset URL (passed in via data attributes, since this file is a
        // plain classic script with no Liquid access of its own) rather
        // than through an import map: Horizon's own theme.liquid already
        // emits one for its native @theme/* module system, and only the
        // first <script type="importmap"> in a document is ever honored —
        // a second one (tried first) was silently ignored, so
        // GLTFLoader.js/BufferGeometryUtils.js/SkeletonUtils.js/
        // RoomEnvironment.js all import three via a relative
        // `./three.module.min.js` instead of the bare "three" specifier,
        // which needs no import map at all.
        Promise.all([
          dynImport(threeUrl),
          dynImport(gltfLoaderUrl)
        ]).then(function (mods) {
          var THREE_NS = mods[0];
          var GLTFLoaderNS = mods[1];
          three.degToRad = THREE_NS.MathUtils.degToRad; // used by render()'s camera math
          var GLTFLoader = GLTFLoaderNS.GLTFLoader;

          var renderer = new THREE_NS.WebGLRenderer({ canvas: canvas, antialias: true, alpha: true });

          // NO shadow maps. I turned them on last round, reversing the
          // original plan, and it was wrong.
          //
          // On four flat stacked slabs a cast shadow lands as a hard dark
          // band along the layer below, and that band reads as an extra
          // layer. It made the core impossible to interpret - the exact
          // opposite of what a construction diagram is for. The original
          // plan called them "a nicety, not structural" and it was right.
          //
          // Separation here comes from thickness, material and the
          // environment map, which is how the reference diagram does it too:
          // look at it and there are no cast shadows between the layers.
          renderer.outputColorSpace = THREE_NS.SRGBColorSpace;
          renderer.toneMapping = THREE_NS.ACESFilmicToneMapping;
          renderer.toneMappingExposure = 1.15;
          renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, 2));

          var sceneGraph = new THREE_NS.Scene();
          sceneGraph.background = null;

          var camera = new THREE_NS.PerspectiveCamera(35, 1, 0.1, 100);

          var light = new THREE_NS.DirectionalLight(0xffffff, 1.1);
          light.position.set(2, 4, 3);
          sceneGraph.add(light);

          // A single directional light plus the environment map still read
          // flat and dull live (checked directly, screenshot + zoom on the
          // deployed page — no visible highlight anywhere, including on the
          // clearcoated waterproofing layer). A dim, cool-toned fill from
          // the opposite side is the cheapest fix: it doesn't add a second
          // hard shadow-casting source, just lifts the side the key light
          // leaves dark so the layers read as separated, lit objects
          // instead of one flat silhouette.
          var fill = new THREE_NS.DirectionalLight(0xdce8ff, 0.45);
          fill.position.set(-3, 1.5, -2);
          sceneGraph.add(fill);

          function sizeToMount() {
            var w = mount.clientWidth || 1;
            var h = mount.clientHeight || 1;
            renderer.setSize(w, h, false);
            camera.aspect = w / h;
            camera.updateProjectionMatrix();
          }
          sizeToMount();

          var loader = new GLTFLoader();
          loader.load(modelSrc, function (gltf) {
            sceneGraph.add(gltf.scene);

            var meshes = {};
            var restY = [];
            LAYER_NODE_NAMES.forEach(function (name, i) {
              var obj = gltf.scene.getObjectByName(name);
              meshes[name] = obj || null;
              restY[i] = obj ? obj.position.y : 0;
              // Y-only scale: thickness is the only thing wrong, and the
              // footprint and UV mapping must not move.
              if (obj) {
                obj.scale.y = obj.scale.y * THICKNESS[i];


                // `Bound_Edge` is a CHILD of the top layer in the GLB, so it
                // inherits this scale and gets flattened with it. The bound
                // edge is the binding tape around the perimeter - it is the
                // most visible detail on the real product photos and it has
                // its own thickness, independent of the face it is sewn to.
                // Undo the parent's Y scale on it so it keeps its own depth.
                var bound = obj.getObjectByName && obj.getObjectByName('Bound_Edge');
                if (bound) bound.scale.y = bound.scale.y / THICKNESS[i];
              }
            });

            /* CLOSE THE STACK WITHOUT LETTING IT INTERSECT ITSELF.

               The GLB's own resting pose has all four layers passing through
               each other. Read from its glTF JSON, world Y extents at rest:

                 Soft_and_Breathable_Fabric      +0.1605 .. +0.2485   (printed)
                 Max_Absorbency_Material         +0.0665 .. +0.2025
                 Breathable_Waterproofing_Layer  +0.0374 .. +0.1046
                 Anti_Slip_Gel_Rubber_Layer      -0.0150 .. +0.0650

               Every pair overlaps - by 0.0420, 0.0381 and 0.0276 top to bottom.
               The absorbent core is 0.136 thick and rises to +0.2025 while the
               printed layer's underside sits at +0.1605, so the white core pokes
               0.042 up THROUGH the printed face. At the 64-degree camera
               elevation the visitor looks down onto the core's surface, not the
               print: the pad reads as a plain cream slab and the print survives
               only on the canting, where the printed layer's edge is still
               exposed.

               That is the frame reported wrong from the start. The camera, the
               box, the sticky frame and the initial active layer were each
               corrected in turn; none of them was this, because none of them
               could be - no camera angle can reveal a face another mesh is
               sitting inside.

               Measured per mesh rather than hard-coded, so a new export cannot
               silently reintroduce it: stack each layer just clear of the one
               below, keeping the model's own order and footprint. */
            (function closeStack() {
              var box = new THREE_NS.Box3();
              var eps = 0.004;                     // a hair of daylight, not a gap
              var cursorTop = null;
              // bottom-up: the array is ordered top-down, so walk it backwards
              for (var i = LAYER_NODE_NAMES.length - 1; i >= 0; i--) {
                var obj = meshes[LAYER_NODE_NAMES[i]];
                if (!obj) continue;
                obj.updateWorldMatrix(true, true);
                box.setFromObject(obj);
                if (cursorTop === null) {
                  cursorTop = box.max.y;           // the bottom layer sets the floor
                } else {
                  var lift = (cursorTop + eps) - box.min.y;
                  if (lift > 0) {
                    obj.position.y += lift;
                    restY[i] += lift;              // the scrub must start from here
                    box.setFromObject(obj.updateWorldMatrix(true, true) || obj);
                  }
                  cursorTop = Math.max(cursorTop, box.max.y);
                }
              }
            })();

            // PROCEDURAL SURFACE TEXTURES.
            //
            // The layers rendered as flat tinted slabs because the GLB ships
            // no maps at all - only base colours. Colour alone cannot make
            // a needle-punched nonwoven look different from a TPU film; what
            // separates them in the reference photo is surface, not hue.
            //
            // Generated here rather than shipped as image assets: each is a
            // few hundred lines of canvas drawing against ~200KB of PNG per
            // layer, and they stay crisp at any zoom because the repeat is
            // tuned per material.
            function makeNormalMap(draw, repeat) {
              var PX = 512;
              var c = document.createElement('canvas');
              c.width = c.height = PX;
              var ctx = c.getContext('2d');
              // Neutral normal: flat surface points straight out.
              ctx.fillStyle = 'rgb(128,128,255)';
              ctx.fillRect(0, 0, PX, PX);
              draw(ctx, PX);
              var t = new THREE_NS.CanvasTexture(c);
              t.wrapS = t.wrapT = THREE_NS.RepeatWrapping;
              t.repeat.set(repeat, repeat);
              if (renderer.capabilities) {
                t.anisotropy = renderer.capabilities.getMaxAnisotropy();
              }
              return t;
            }

            // Woven cloth: fine over-under threads, the texture you see when
            // a quilted face catches raking light.
            var weaveMap = makeNormalMap(function (ctx, PX) {
              var step = 6;
              for (var i = 0; i < PX; i += step) {
                ctx.strokeStyle = 'rgba(150,128,255,0.55)';
                ctx.lineWidth = 2;
                ctx.beginPath(); ctx.moveTo(i, 0); ctx.lineTo(i, PX); ctx.stroke();
                ctx.strokeStyle = 'rgba(128,150,255,0.55)';
                ctx.beginPath(); ctx.moveTo(0, i); ctx.lineTo(PX, i); ctx.stroke();
              }
            }, 26);

            // Needle-punched nonwoven: matted fibre, no direction, no weave.
            // Random short strokes rather than a grid, because a grid is
            // exactly what this material does not have.
            var fibreMap = makeNormalMap(function (ctx, PX) {
              for (var n = 0; n < 5200; n++) {
                var x = Math.random() * PX, y = Math.random() * PX;
                var a = Math.random() * Math.PI, len = 4 + Math.random() * 9;
                ctx.strokeStyle = 'rgba(' + (108 + Math.random() * 40) + ',' +
                  (108 + Math.random() * 40) + ',255,0.4)';
                ctx.lineWidth = 1;
                ctx.beginPath();
                ctx.moveTo(x, y);
                ctx.lineTo(x + Math.cos(a) * len, y + Math.sin(a) * len);
                ctx.stroke();
              }
            }, 14);

            // REAL PBR MAPS.
            //
            // Photographed material scans beat anything drawn on a canvas -
            // real fibre has irregularity that procedural noise only
            // approximates. Both are CC0 from ambientCG, so no attribution
            // is required and nothing here is licence-encumbered:
            //
            //   Fabric082A  fine grey fabric  -> the needle-punched core
            //   Rubber004   gym floor rubber  -> the silicone backing
            //
            // NormalGL, not NormalDX: three.js uses the OpenGL green-channel
            // convention, and the DX variant renders the bumps inverted.
            //
            // Resized to 512 and re-encoded before shipping - the 1K set is
            // 3.6MB across four files, which would have undone the image
            // payload work. At 512 the whole set is 240KB.
            // A LoadingManager, so the reveal below can wait for the maps.
            //
            // `TextureLoader.load` is asynchronous and returns an empty texture
            // immediately. The model used to be revealed and painted on the
            // same tick, so the first frame drew the material with no map on
            // it: one grey frame before the print appeared. Reported by the
            // owner as "3d nu lucreaza", and it is a single frame, which is why
            // it never showed up in a scripted screenshot.
            var texManager = new THREE_NS.LoadingManager();
            var texLoader = new THREE_NS.TextureLoader(texManager);
            function loadMap(url, srgb) {
              var t = texLoader.load(url);
              t.wrapS = t.wrapT = THREE_NS.RepeatWrapping;
              if (renderer.capabilities) {
                t.anisotropy = renderer.capabilities.getMaxAnisotropy();
              }
              // Normal and roughness maps are data, not colour. Tagging them
              // sRGB would gamma-shift the values and flatten the surface.
              if (srgb && THREE_NS.SRGBColorSpace) t.colorSpace = THREE_NS.SRGBColorSpace;
              return t;
            }
            var PBR = (mount && mount.dataset && mount.dataset.pbrBase) || '';
            var quiltN    = PBR ? loadMap(PBR + 'pp-quilt-normal.jpg') : null;
            var quiltR    = PBR ? loadMap(PBR + 'pp-quilt-rough.jpg') : null;
            var nonwovenN = PBR ? loadMap(PBR + 'pp-nonwoven-normal.jpg') : null;
            var nonwovenR = PBR ? loadMap(PBR + 'pp-nonwoven-rough.jpg') : null;
            var siliconeN = PBR ? loadMap(PBR + 'pp-silicone-normal.jpg') : null;
            var siliconeR = PBR ? loadMap(PBR + 'pp-silicone-rough.jpg') : null;
            // Every layer gets its OWN scan at its OWN repeat. The previous
            // pass put Fabric082A on both the face and the core, so two of
            // the four layers were literally the same texture - which is
            // exactly what "totul pare sa fie aceeasi textura" describes.
            // Fabric019 is a visible weave; Fabric082A is fine matted fibre.
            [quiltN, quiltR].forEach(function (t) { if (t) t.repeat.set(4, 4); });
            [nonwovenN, nonwovenR].forEach(function (t) { if (t) t.repeat.set(16, 16); });
            [siliconeN, siliconeR].forEach(function (t) { if (t) t.repeat.set(7, 7); });

            // Open-cell dimple map for the absorbent core.
            //
            // The owner's diagram shows this layer as a pale, pitted surface
            // - visibly cellular, like a sponge cut open. That is what a
            // 280 gsm needle-punched nonwoven looks like in section, and it
            // is the single feature that was missing: ours rendered as a
            // smooth dark slab, which is why it read as the least finished
            // layer of the four.
            //
            // Drawn as a normal map of staggered pits rather than modelled,
            // for the same reason as the backing dots - it only has to read
            // as texture at this distance.
            var dimpleMap = (function () {
              var PX = 512, c = document.createElement('canvas');
              c.width = c.height = PX;
              var ctx = c.getContext('2d');
              ctx.fillStyle = 'rgb(128,128,255)';
              ctx.fillRect(0, 0, PX, PX);
              var pitch = 20, r = 7, row = 0;
              for (var y = 0; y < PX + pitch; y += pitch * 0.86) {
                // Staggered rows, so the cells pack like foam rather than
                // sitting on a square grid, which reads as a manufactured
                // perforation instead of a fibre structure.
                var off = (row % 2) ? pitch / 2 : 0;
                for (var x = -pitch; x < PX + pitch; x += pitch) {
                  var g = ctx.createRadialGradient(x + off, y, 1, x + off, y, r);
                  g.addColorStop(0, 'rgba(96,96,220,0.95)');
                  g.addColorStop(0.7, 'rgba(150,150,255,0.55)');
                  g.addColorStop(1, 'rgba(128,128,255,0)');
                  ctx.fillStyle = g;
                  ctx.beginPath();
                  ctx.arc(x + off, y, r, 0, Math.PI * 2);
                  ctx.fill();
                }
                row++;
              }
              var t = new THREE_NS.CanvasTexture(c);
              t.wrapS = t.wrapT = THREE_NS.RepeatWrapping;
              t.repeat.set(7, 7);
              if (renderer.capabilities) t.anisotropy = renderer.capabilities.getMaxAnisotropy();
              return t;
            })();

            // HEIGHT MAP for parallax. Same staggered cells as the normal
            // map, but as depth rather than slope: white is the surface,
            // black is the bottom of a pit.
            var heightMap = (function () {
              var PX = 512, c = document.createElement('canvas');
              c.width = c.height = PX;
              var ctx = c.getContext('2d');
              ctx.fillStyle = '#fff';
              ctx.fillRect(0, 0, PX, PX);
              var pitch = 20, r = 8, row = 0;
              for (var y = 0; y < PX + pitch; y += pitch * 0.86) {
                var off = (row % 2) ? pitch / 2 : 0;
                for (var x = -pitch; x < PX + pitch; x += pitch) {
                  var g = ctx.createRadialGradient(x + off, y, 0, x + off, y, r);
                  g.addColorStop(0, '#2b2b2b');
                  g.addColorStop(0.75, '#b8b8b8');
                  g.addColorStop(1, '#ffffff');
                  ctx.fillStyle = g;
                  ctx.beginPath();
                  ctx.arc(x + off, y, r, 0, Math.PI * 2);
                  ctx.fill();
                }
                row++;
              }
              var t = new THREE_NS.CanvasTexture(c);
              t.wrapS = t.wrapT = THREE_NS.RepeatWrapping;
              t.repeat.set(7, 7);
              return t;
            })();

            // PARALLAX OCCLUSION MAPPING.
            //
            // The pitted cells in the reference diagram sit on the CUT EDGE
            // of the layer, not its face - you see them because the material
            // is sliced. A normal map cannot produce that: it only tilts the
            // lighting, it never moves anything, so at a grazing angle the
            // surface stays visibly flat.
            //
            // POM steps a ray through a height map per pixel and offsets the
            // texture read by however deep it got, which is real apparent
            // depth on flat geometry. It costs shader work rather than
            // vertices - and vertices are what we do not have: each layer is
            // ~2,868 of them, nowhere near enough to displace.
            //
            // Tangent frame is derived from screen-space derivatives because
            // the GLB carries no TANGENT attribute. Steps are kept low and
            // scaled by view angle: head-on needs almost none, grazing needs
            // the most, which is exactly where we are looking.
            function applyParallax(mat, hMap, depth) {
              mat.onBeforeCompile = function (shader) {
                shader.uniforms.pomMap = { value: hMap };
                shader.uniforms.pomDepth = { value: depth };
                shader.fragmentShader = shader.fragmentShader
                  .replace('#include <common>', [
                    '#include <common>',
                    'uniform sampler2D pomMap;',
                    'uniform float pomDepth;',
                    'vec2 pomOffset(vec2 uv, vec3 viewDir) {',
                    '  float nSteps = mix(24.0, 6.0, abs(viewDir.z));',
                    '  float layerD = 1.0 / nSteps;',
                    '  float curD = 0.0;',
                    '  vec2 P = viewDir.xy / max(abs(viewDir.z), 0.15) * pomDepth;',
                    '  vec2 dUV = P / nSteps;',
                    '  vec2 cur = uv;',
                    '  float h = 1.0 - texture2D(pomMap, cur).r;',
                    '  for (int i = 0; i < 24; i++) {',
                    '    if (curD >= h) break;',
                    '    cur -= dUV;',
                    '    h = 1.0 - texture2D(pomMap, cur).r;',
                    '    curD += layerD;',
                    '  }',
                    // One linear step back between the last two samples, so
                    // the silhouette does not stair-step at low step counts.
                    '  vec2 prev = cur + dUV;',
                    '  float after = h - curD;',
                    '  float before = (1.0 - texture2D(pomMap, prev).r) - curD + layerD;',
                    '  float w = after / (after - before);',
                    '  return mix(cur, prev, clamp(w, 0.0, 1.0));',
                    '}'
                  ].join('\n'))
                  .replace('#include <map_fragment>', [
                    '#ifdef USE_MAP',
                    '#endif',
                    'vec3 pomView = normalize(vViewPosition);',
                    'vec2 pomUv = pomOffset(vNormalMapUv, pomView);',
                    '#include <map_fragment>'
                  ].join('\n'))
                  // Sample the normal map at the parallax-shifted coordinate
                  // so lighting agrees with the displaced surface.
                  .replace('vec3 mapN = texture2D( normalMap, vNormalMapUv ).xyz * 2.0 - 1.0;',
                           'vec3 mapN = texture2D( normalMap, pomUv ).xyz * 2.0 - 1.0;');
              };
              mat.needsUpdate = true;
            }

            // MATERIAL PASS.
            //
            // Matched to the owner's reference diagram, adapted to our own
            // spec rather than copied: their backing is a diamond mesh, ours
            // is round silicone dots, because that is what the product has.
            //
            // What the reference gets right and the GLB did not: the four
            // layers are four different *materials*, not four tinted slabs.
            // A matte fibrous core beside a smooth film beside a rubbery
            // grip is what makes the diagram legible at a glance.
            (function materials() {
              function each(mesh, fn) {
                if (!mesh || !mesh.material) return;
                (Array.isArray(mesh.material) ? mesh.material : [mesh.material]).forEach(fn);
              }

              // 1. Printed face - quilted, so it should not be flat. Slight
              //    sheen, but nothing glossy: it is a fabric you sleep on.
              each(meshes[LAYER_NODE_NAMES[0]], function (m) {
                m.normalMap = quiltN || weaveMap;
                if (quiltR) m.roughnessMap = quiltR;
                // Coarse and deep: a quilted face has visible relief, and it
                // must not read like the fine fibre directly beneath it.
                if (m.normalScale) m.normalScale.set(1.4, 1.4);
                m.roughness = 0.62;
                // Lighter sheen than the core: a printed quick-dry mesh has
                // some fibre lift, but nothing like matted felt. Same
                // mechanism, quarter the strength, so the two never read as
                // the same material.
                if (m.sheen !== undefined) {
                  m.sheen = 0.35;
                  m.sheenRoughness = 0.55;
                  if (m.sheenColor) m.sheenColor.setHex(0xffffff);
                }
                m.metalness = 0;
                if (m.clearcoat !== undefined) m.clearcoat = 0.08;
                m.needsUpdate = true;
              });

              // 2. Absorbent core - the thick needle-punched nonwoven. In the
              //    reference this is the darkest, flattest layer, and that
              //    contrast is what separates it from the film below. Ours
              //    rendered near-white and merged with its neighbour.
              each(meshes[LAYER_NODE_NAMES[1]], function (m) {
                // Pale, not dark. The owner mapped our core onto the
                // diagram's third layer, which is near-white - and an
                // undyed polyester/viscose nonwoven really is.
                m.normalMap = dimpleMap;
                if (nonwovenR) m.roughnessMap = nonwovenR;
                // Fine and shallow against the face's coarse relief. Same
                // family of material, deliberately different scale, so the
                // eye reads two fabrics rather than one repeated.
                // Deep, because the pitting is the whole point.
                if (m.normalScale) m.normalScale.set(1.6, 1.6);
                // 0.035 of a UV unit. Enough to read as real depth at a
                // grazing angle without the silhouette swimming when the
                // model is dragged.
                applyParallax(m, heightMap, 0.035);
                if (m.color) m.color.setHex(0xeae6dd);
                m.roughness = 0.97;
                m.metalness = 0;
                if (m.clearcoat !== undefined) m.clearcoat = 0;

                // SHEEN - the cloth BRDF, and the thing that was missing.
                //
                // Roughness and a normal map alone render felt as grey
                // plastic. What reads as felt is retroreflection: light
                // catching the fibre ends at grazing angles, which is why
                // reference felt materials "showcase every fiber when
                // backlit". three.js models exactly this with `sheen`, and
                // no amount of texture substitutes for it.
                //
                // Warm, near-white sheen against the cool grey base: real
                // needle-punched polyester/viscose is not dyed through, so
                // the fibre tips catch lighter than the body of the felt.
                if (m.sheen !== undefined) {
                  m.sheen = 1.0;
                  m.sheenRoughness = 0.85;
                  if (m.sheenColor) m.sheenColor.setHex(0xd8d2c8);
                }
                m.needsUpdate = true;
              });

              // 3. TPU barrier - a film. Smooth, faintly translucent, with a
              //    little specular so it reads as plastic sheet rather than
              //    another piece of cloth. It was tinted pale blue, which
              //    made it look like a third fabric.
              // 3. TPU barrier - TRANSPARENT, not an opaque pale slab.
              //
              // Industry sources put waterproof-breathable TPU membrane at
              // 0.012-0.05 mm and describe it as transparent to translucent,
              // non-porous, smooth. Ours is 0.025 mm, dead centre of that
              // band. A film that thin cannot read as a solid sheet.
              //
              // This also settles the contradiction between the reference
              // diagram and the spec sheet: the diagram draws this layer
              // thick and white so it is visible in an illustration. The
              // physical part is a film you can see through. We follow the
              // spec, because the site states 0.025 mm as fact.
              each(meshes[LAYER_NODE_NAMES[2]], function (m) {
                // Darker and smooth, matching the diagram's second layer,
                // which the owner mapped onto this one. Kept thin and still
                // slightly translucent, because the spec says 0.025 mm.
                if (m.color) m.color.setHex(0x8f959b);
                m.roughness = 0.16;
                m.metalness = 0;
                m.transparent = true;
                m.opacity = 0.82;
                // Physical transmission where the material supports it: a
                // film refracts what is behind it rather than just fading.
                if (m.transmission !== undefined) {
                  m.transmission = 0.25;
                  m.thickness = 0.02;
                  m.ior = 1.45;
                }
                if (m.clearcoat !== undefined) {
                  m.clearcoat = 0.85;
                  m.clearcoatRoughness = 0.06;
                }
                // Both sides, or the film vanishes when seen from beneath.
                m.side = THREE_NS.DoubleSide;
                // depthWrite stays ON. Turning it off let every layer behind
                // the film draw over it, which is the overlapping the owner
                // saw. A film is thin, not order-independent.
                m.depthWrite = true;
                m.needsUpdate = true;
              });

              // 4. Silicone backing - darker and rubbery, so the dot grid
              //    applied below has something to sit on. Grip, not gloss.
              each(meshes[LAYER_NODE_NAMES[3]], function (m) {
                if (siliconeN) {
                  m.normalMap = siliconeN;
                  if (m.normalScale) m.normalScale.set(0.9, 0.9);
                }
                if (siliconeR) m.roughnessMap = siliconeR;
                if (m.color) m.color.setHex(0x6b7075);
                m.metalness = 0;
                m.needsUpdate = true;
              });
            })();

            // The backing's dot grid. 2-3 mm silicone dots on a 6-7 mm pitch
            // at 18-25% coverage, hot-pressed - the most recognisable
            // physical feature of the product, and completely absent from
            // the model, which carried a flat surface.
            //
            // Drawn to a canvas and used as a bump/roughness map rather than
            // modelled as geometry: thousands of 2 mm dots would be a real
            // triangle cost for something that only needs to read as texture
            // at this camera distance.
            (function applyDotGrid() {
              var back = meshes[LAYER_NODE_NAMES[3]];
              if (!back || !back.material) return;
              var PX = 512;
              // 6.5 mm pitch across a 610 mm pad edge -> ~94 dots per side.
              var PITCH = PX / 94;
              var R = PITCH * 0.36; // 2.35 mm dot against a 6.5 mm pitch
              var c = document.createElement('canvas');
              c.width = c.height = PX;
              var ctx = c.getContext('2d');
              ctx.fillStyle = '#000';
              ctx.fillRect(0, 0, PX, PX);
              ctx.fillStyle = '#fff';
              for (var y = PITCH / 2; y < PX; y += PITCH) {
                for (var x = PITCH / 2; x < PX; x += PITCH) {
                  ctx.beginPath();
                  ctx.arc(x, y, R, 0, Math.PI * 2);
                  ctx.fill();
                }
              }
              var tex = new THREE_NS.CanvasTexture(c);
              tex.wrapS = tex.wrapT = THREE_NS.RepeatWrapping;
              tex.repeat.set(6, 6);
              if (renderer.capabilities) {
                tex.anisotropy = renderer.capabilities.getMaxAnisotropy();
              }
              var mats = Array.isArray(back.material) ? back.material : [back.material];
              mats.forEach(function (m) {
                m.bumpMap = tex;
                // 0.3-0.5 mm proud of the surface. Small, because it is a
                // grip texture and not a tread pattern.
                m.bumpScale = 0.35;
                // Silicone is matte where the dots sit and slicker between
                // them, which is what makes the grid catch the light.
                m.roughnessMap = tex;
                m.roughness = 0.85;
                m.needsUpdate = true;
              });
            })();

            // Environment lighting for the clearcoated waterproofing
            // layer's MeshPhysicalMaterial: clearcoat is a reflection
            // effect, and reads flat/matte with plain lights alone because
            // there is nothing for the clearcoat lobe to reflect. Render
            // RoomEnvironment's small procedural studio once through a
            // PMREM generator and assign it as scene.environment (not
            // .background — the page background stays visible through the
            // canvas, matching the transparent poster this replaces).
            dynImport(roomEnvUrl).then(function (RoomEnvNS) {
              var pmrem = new THREE_NS.PMREMGenerator(renderer);
              var envScene = new RoomEnvNS.RoomEnvironment();
              sceneGraph.environment = pmrem.fromScene(envScene, 0.04).texture;
              pmrem.dispose();
              finishMount();
            }).catch(finishMount); // environment is an enhancement, not a hard requirement

            function finishMount() {
              // Derive camera radius/target mechanically instead of
              // reusing <model-viewer>'s old numbers, which were tuned
              // around the broken (much larger) explode distance and had
              // the wrong axis dominating the framing. Temporarily pose the
              // model fully exploded, measure it, then reset to rest
              // before the first real frame paints.
              LAYER_NODE_NAMES.forEach(function (name, i) {
                if (meshes[name]) meshes[name].position.y = restY[i] + EXPLODE[i];
              });
              var box = new THREE_NS.Box3().setFromObject(gltf.scene);
              var sphere = box.getBoundingSphere(new THREE_NS.Sphere());
              LAYER_NODE_NAMES.forEach(function (name, i) {
                if (meshes[name]) meshes[name].position.y = restY[i];
              });

              var fovRad = THREE_NS.MathUtils.degToRad(camera.fov);
              // 1.35 read small/timid live (screenshot-checked against the
              // deployed page: the model filled well under half the mount's
              // width, most of the box was empty white space). Tightened so
              // the model reads as the confident focal point of its own
              // section instead of a small object adrift in a big box.
              // 0.92, not 1.12. The bounding sphere is measured with every
              // layer at full explode, so padding sized for that state leaves
              // the model small at every other scroll position - and most of
              // the time the reader is at another position. Sized so the
              // exploded state just fills the frame instead of floating in it.
              // 0.82. The crop and the separation trade against each other,
              // and this is where they balance.
              //
              // 0.44 was sized for a nearly-closed stack. With the layers now
              // spread far enough for their top faces to show, that crop only
              // held two of the four at a time - the printed face scrolled
              // out of frame entirely. Widened until all four sit in shot
              // with their faces still visible, which is the whole point of
              // having spread them.
              /* The factor above this line was retuned five times by eye
                 (1.35, 1.12, 0.92, 0.82, 0.44, 0.82) and every value was
                 measured on a box that no longer exists. The mount is square
                 only on the stacked layout; at desktop `.pp-lyr__grid > .pp-3d`
                 overrides it to `inset: 0; height: 100%`, and an explicit height
                 beats `aspect-ratio`, so the real box is 1280x718 - a 16:9.

                 A factor below 1 puts the camera nearer than the fit distance,
                 which is a deliberate crop. On a square box it trimmed the
                 corners; on a 16:9 box the vertical is the binding axis and the
                 same 0.82 cut the bottom of the pad out of frame entirely -
                 which is the frame where the pad cannot be seen at all.

                 Fit both axes and take whichever is further, so the framing no
                 longer depends on the box's aspect, then crop once and
                 deliberately. `hFov` is the horizontal field of view derived
                 from the vertical one and the live aspect. */
              /* One camera distance cannot frame two subjects of very different
                 size, and that is what this section asks for: at rest the stack
                 is a closed pad a few centimetres thick, at full scrub it is
                 four layers spread apart. Every earlier value here was a single
                 number sized for one of those states, which is why it was
                 retuned five times and why the resting frame read as a small
                 object adrift in a large empty box.

                 Measure both states and interpolate with the same progress the
                 explode uses, so the subject fills the frame throughout. */
              function fitDistance(r) {
                var hFovRad = 2 * Math.atan(Math.tan(fovRad / 2) * (camera.aspect || 1));
                return Math.max(r / Math.sin(fovRad / 2), r / Math.sin(hFovRad / 2));
              }

              var padding = 0.95;
              three.radiusOpen = Math.max(0.5, fitDistance(sphere.radius) * padding);

              // the resting stack, measured with the layers back at restY
              var restBox = new THREE_NS.Box3().setFromObject(gltf.scene);
              var restSphere = restBox.getBoundingSphere(new THREE_NS.Sphere());
              three.radiusRest = Math.max(0.5, fitDistance(restSphere.radius) * padding);
              three.radius = three.radiusRest;
              // Aim at a corner of the stack, not its centre. Offset by a
              // third of the bounding radius on X and Z so the near corner
              // fills the frame and the far edge leaves it, the way the
              // reference is composed. Y stays centred so the whole stack
              // remains vertically in shot.
              // Centred on X now. The earlier corner offset pushed the stack
              // into the right half; with the panel overlapping it, the
              // stack needs to sit across the middle so it reads both
              // through the glass and in the clear space beside it. Z keeps
              // a small offset so the near edge still runs off frame.
              three.target = sphere.center.clone();
              three.target.z += sphere.radius * 0.22;
              three.spherical = new THREE_NS.Spherical();
              three.renderer = renderer;
              three.scene = sceneGraph;
              three.camera = camera;
              three.meshes = meshes;
              three.restY = restY;
              three.canvas = canvas;
              three.ready = true;

              // Reveal once the maps are in, not once the canvas exists.
              //
              // The safety timeout matters as much as the wait: if a map 404s
              // or the network stalls, `onLoad` never fires and the model would
              // stay hidden behind the CSS stack forever. 2.5s is well past a
              // 240KB texture set on any connection that loaded the page.
              var revealed = false;
              function revealModel() {
                if (revealed) return;
                revealed = true;
                scene.classList.add('has-model');
              }
              texManager.onLoad = revealModel;
              setTimeout(revealModel, 2500);
              // Nothing left to wait for: reveal on the next frame so the
              // renderer has painted once with the maps already applied.
              if (!texManager.itemsTotal || texManager.itemsLoaded >= texManager.itemsTotal) {
                requestAnimationFrame(revealModel);
              }

              // Paint immediately, at the current scroll position.
              //
              // Rendering only happened inside the scroll loop, and that loop
              // only runs while the section is being scrolled through. Load
              // the model while the block is already in view and standing
              // still - which is what happens when the reveal finishes - and
              // nothing drew until the next scroll event. The canvas sat
              // blank or half-built for the first frames.
              //
              // Two frames: the first commits geometry and material updates,
              // the second draws with the textures resolved.
              render(progress);
              requestAnimationFrame(function () {
                if (three && three.ready) render(progress);
              });

              bindDrag(canvas);

              if ('ResizeObserver' in window) {
                new ResizeObserver(function () {
                  sizeToMount();
                }).observe(mount);
              }

              render(progress);
            }
          }, undefined, function (err) {
            // load error: fall back to the CSS stack, matching the old
            // <model-viewer> 'error' event contract.
            if (window.console) console.error('pp-3d: GLB load failed', err);
            scene.classList.remove('has-model');
            three = null;
          });
        }).catch(function (err) {
          if (window.console) console.error('pp-3d: mount failed', err);
          scene.classList.remove('has-model');
          three = null;
        });
      }

      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              if (e.isIntersecting) {
                mountModel();
                io.disconnect();
              }
            });
          },
          { rootMargin: '300px 0px' }
        );
        io.observe(scene);
      } else {
        mountModel();
      }

      if (staticPose) {
        /* Small screens and reduced-motion get a static pose - there is no pin
           below 800px, so there is no scrub to drive. But the copy shown here
           says "Tap a layer to read what it does", and until now nothing was
           listening: this `return` sits above the step-button handler further
           down the file, so on a phone that handler was never attached at all.
           The promise in the text had no implementation behind it.

           Tapping a layer now moves to that layer's slice directly. `render`
           already does both halves of the job - it poses the meshes and calls
           setActive, which reveals that layer's description card - so the tap
           needs no separate path, just the progress value that centres on the
           layer:  p = ((n + 0.5) / count) * 0.84, the same mapping the scroll
           uses in reverse. */
        steps.forEach(function (btn, n) {
          btn.addEventListener('click', function () {
            render(((n + 0.5) / count) * 0.84);
          });
        });
        render(((0 + 0.5) / count) * 0.84);
        return;
      }

      /* ---------- scroll ----------
         Driven by a rAF loop that runs only while the section is in view,
         reading getBoundingClientRect each frame, rather than by a scroll
         listener on window. Deliberate: a scroll listener assumes the
         document itself is the scroller, which is not guaranteed (themes,
         embedded previews, and app-embed wrappers can move scrolling to a
         container, and `window.scrollY` then never changes). The rect is
         true regardless of which element actually scrolls, and the loop
         idles to zero cost whenever the section is off-screen. Proven
         correct in the model-viewer build — unchanged here. */
      var rafId = null;
      var inView = false;

      var lastT = 0;

      function readTarget() {
        var r = track.getBoundingClientRect();
        var travel = r.height - window.innerHeight;
        var p = travel > 0 ? -r.top / travel : 0;
        target = Math.max(0, Math.min(1, p));
      }

      function measure() {
        // Used for resize and first paint: snap, don't spring, so the scene
        // is correct immediately rather than easing in from wherever it was.
        readTarget();
        shown = target;
        vel = 0;
        render(shown);
      }

      function loop(t) {
        if (!inView) {
          rafId = null;
          lastT = 0;
          return;
        }
        // Real elapsed time, clamped: a background tab or a slow frame must
        // not hand the spring a huge dt and make it overshoot wildly.
        var dt = lastT ? Math.min((t - lastT) / 1000, 1 / 30) : 1 / 60;
        lastT = t;
        readTarget();
        render(stepSpring(dt));
        rafId = requestAnimationFrame(loop);
      }

      function setInView(v) {
        inView = v;
        if (v && rafId === null) rafId = requestAnimationFrame(loop);
      }

      if ('IntersectionObserver' in window) {
        new IntersectionObserver(
          function (entries) {
            entries.forEach(function (e) {
              setInView(e.isIntersecting);
            });
          },
          { rootMargin: '200px 0px' }
        ).observe(track);
      } else {
        setInView(true);
      }

      window.addEventListener('resize', measure);
      measure();

      /* ---------- drag: shared by the CSS stage and, once mounted, the 3D
         canvas. mount's own CSS already makes the two mutually exclusive
         via pointer-events, so binding both is safe. ---------- */
      var dragging = false;
      var lastX = 0;
      var lastY = 0;

      function bindDrag(el) {
        el.addEventListener('pointerdown', function (e) {
          dragging = true;
          lastX = e.clientX;
          lastY = e.clientY;
          el.setPointerCapture(e.pointerId);
          scene.classList.add('is-dragging');
        });

        el.addEventListener('pointermove', function (e) {
          if (!dragging) return;
          dragY += (e.clientX - lastX) * 0.4;
          dragX -= (e.clientY - lastY) * 0.3;
          lastX = e.clientX;
          lastY = e.clientY;
          measure();
        });

        function endDrag(e) {
          if (!dragging) return;
          dragging = false;
          if (e && e.pointerId != null && el.hasPointerCapture(e.pointerId)) {
            el.releasePointerCapture(e.pointerId);
          }
          scene.classList.remove('is-dragging');
        }

        el.addEventListener('pointerup', endDrag);
        el.addEventListener('pointercancel', endDrag);
      }

      bindDrag(stage);

      /* ---------- step buttons jump to that layer's slice ---------- */
      /* The scroll container is `.page-wrapper`, not the window: Horizon sets
         `overflow: hidden` on both html and body and scrolls that div instead.
         `window.scrollTo` therefore did nothing at all, which is why tapping a
         layer had no effect - the handler ran, the target was right, and the
         scroll went to an element that cannot scroll. Verified live:
         html and body report overflowY: hidden with scrollHeight == clientHeight,
         while .page-wrapper reports overflowY: auto and 18579/1100.

         `offsetTop` is measured against the offset parent, so the target is
         converted through the container's own coordinate space rather than the
         page's. */
      var scroller = document.querySelector('.page-wrapper') ||
                     document.scrollingElement || document.documentElement;

      steps.forEach(function (btn, n) {
        btn.addEventListener('click', function () {
          var travel = track.offsetHeight - scroller.clientHeight;
          if (travel <= 0) return;
          var target = 0.16 + ((n + 0.5) / count) * 0.84;
          var trackTop = track.getBoundingClientRect().top -
                         scroller.getBoundingClientRect().top + scroller.scrollTop;
          scroller.scrollTo({ top: trackTop + travel * target, behavior: 'smooth' });
        });
      });
    } catch (err) {
      scene.classList.remove('is-live');
    }
  });
})();
